import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import TwoColumnLayout from "../../components/layouts/TwoColumnLayout";
import ListItem from "../../components/list-item/ListItem";
import leftColumnList from "./leftColumnList";
import rightColumnList from "./rightColumnList";
import {
  useSignoutUserMutation,
  useGetCurrentUserQuery,
} from "../../app/accounts/authuser";
import { useGetFollowersQuery } from "../../app/services/follows";
import { useGetMyRolesQuery } from "../../app/services/roles";
import { useLazyGetHelpCenterQuery } from "../../app/services/notes";
import {
  OWNER_HELP_CENTER_URL,
  PARTNER_HELP_CENTER_URL,
} from "../../constant";

const MyPage = () => {
  const location = useLocation();
  const requestedTab = location.state?.selectedItem;
  const initialTab =
    requestedTab && rightColumnList[requestedTab] ? requestedTab : "paymentDetails";

  const { data: currentUser } = useGetCurrentUserQuery();
  const role = currentUser?.data?.role;
  const isClient = role === "CLIENT";

  const { data: myRolesData } = useGetMyRolesQuery(undefined, {
    skip: isClient,
  });
  const { data: followersData } = useGetFollowersQuery({
    limit: 1,
    offset: 0,
  });

  const [selectedItem, setSelectedItem] = useState(initialTab);

  const [layoutSetShowRightColumn, setLayoutSetShowRightColumn] = useState(null);
  const [signoutUser] = useSignoutUserMutation();
  const [fetchHelpCenter] = useLazyGetHelpCenterQuery();
  const navigate = useNavigate();

  // TwoColumnLayoutからsetShowRightColumn関数を受け取るコールバック
  const getSetShowRightColumn = useCallback((setShowRightColumnFunc) => {
    setLayoutSetShowRightColumn(() => setShowRightColumnFunc);
  }, []);

  useEffect(() => {
    if (role === "CLIENT") {
      setSelectedItem("notification");
    }
  }, [role]);

  useEffect(() => {
    if (!requestedTab || !rightColumnList[requestedTab]) return;
    setSelectedItem(requestedTab);
    if (layoutSetShowRightColumn) {
      layoutSetShowRightColumn(true);
    }
  }, [requestedTab, layoutSetShowRightColumn]);

  // サインアウト処理
  const handleSignout = async () => {
    try {
      await signoutUser().unwrap();
    } catch (error) {
      console.error("サインアウトエラー:", error);
      // エラーでも強制的にリダイレクト（フォールバック処理は mutation 内で実行される）
    }
  };

  // ヘルプセンター: ロールに応じて割り当てられた外部公開ノートセクションへ
  // 遷移する。?section= で割当セクションのみを表示する（共有ノート全体は
  // 見せない）。未割当（または解決失敗）時は既存の Notion ヘルプセンターを
  // 新規タブで開く（CLIENT = Owner / それ以外 = Partner）。
  const handleHelpCenter = async () => {
    try {
      const result = await fetchHelpCenter().unwrap();
      const { section_id, first_note_id } = result?.data ?? {};
      if (section_id) {
        const suffix = `?section=${encodeURIComponent(section_id)}`;
        navigate(
          first_note_id ? `/notes/${first_note_id}${suffix}` : `/notes${suffix}`,
        );
        return;
      }
    } catch {
      // 解決に失敗した場合も Notion フォールバックへ進む
    }
    const url = isClient ? OWNER_HELP_CENTER_URL : PARTNER_HELP_CENTER_URL;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderListItems = (items) => {
    return items.map((item) => (
      <ListItem
        key={item.id}
        name={item.name}
        status={item.status}
        statusTone={item.statusTone}
        alert={item.alert}
        onClick={item.onClick}
        size={item.size}
        isSelected={selectedItem === item.id}
        hasBorderBottom={item.hasBorderBottom}
      />
    ));
  };

  const leftColumnProps = {
    setSelectedItem,
    setShowRightColumn: value => {
      if (layoutSetShowRightColumn) {
        layoutSetShowRightColumn(value);
      }
    },
    handleSignout,
    handleHelpCenter,
    role,
    userData: currentUser?.data,
    myRolesCount: myRolesData?.data?.count,
    followersCount: followersData?.data?.count,
  };

  // 左カラムのコンテンツ
  const leftColumnContent = renderListItems(
    leftColumnList(leftColumnProps)
  );

  // 右カラムのコンテンツ（常にメニューと紐づく情報を表示）
  const rightColumnContent = rightColumnList[selectedItem];

  return (
    <TwoColumnLayout
      leftColumnContent={leftColumnContent}
      rightColumnContent={rightColumnContent}
      getSetShowRightColumn={getSetShowRightColumn}
      initialShowRightColumn={initialTab !== "paymentDetails"}
    />
  );
};

export default MyPage;
